/**
 * First-touch ad attribution.
 *
 * When a visitor lands from an ad link (utm_* parameters, Meta `fbclid`,
 * Google `gclid`), we keep where they came from in this browser so that,
 * if they sign up later (even after browsing other pages), the merchant
 * account records the campaign that brought them.
 *
 * First touch wins for 30 days: a later organic visit does not erase the ad
 * that brought them. Storage can be unavailable (private mode, blocked):
 * every access is guarded and the site works the same without it.
 */

const KEY = 'lp_acquisition';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const UTM = ['source', 'medium', 'campaign', 'content', 'term'];

const clip = (v, n) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, n) : undefined);

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.first_seen_at || Date.now() - Date.parse(data.first_seen_at) > TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

/* Call once per page load. Returns the stored attribution (or null). */
export function captureAttribution() {
  try {
    const existing = read();
    if (existing) return existing;
    const params = new URLSearchParams(window.location.search);
    const data = {};
    UTM.forEach((k) => { const v = clip(params.get(`utm_${k}`), k === 'source' || k === 'medium' ? 100 : 150); if (v) data[k] = v; });
    const fbclid = clip(params.get('fbclid'), 300);
    const gclid = clip(params.get('gclid'), 300);
    if (fbclid) data.fbclid = fbclid;
    if (gclid) data.gclid = gclid;
    if (!Object.keys(data).length) return null; // organic visit: nothing to remember
    if (!data.source) data.source = fbclid ? 'facebook' : gclid ? 'google' : undefined;
    data.landing = clip(window.location.pathname, 200);
    let ref;
    try { ref = document.referrer ? new URL(document.referrer).host : undefined; } catch { ref = undefined; }
    if (ref) data.referrer = clip(ref, 200);
    data.first_seen_at = new Date().toISOString();
    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
    window.localStorage.setItem(KEY, JSON.stringify(data));
    return data;
  } catch {
    return null;
  }
}

export function getAttribution() {
  return read();
}
